/* eslint-disable no-mixed-operators,object-shorthand,max-len */
import { get, findIndex, snakeCase } from 'lodash';
import { mapActions, mapState } from 'vuex';
import { formMixin, validators } from '@/components/forms/mixin';
import popupPageModeMixin from '@/mixins/popupPageModeMixin';
import userAgentMixin from '@/mixins/userAgent';
import { MODALS } from '@/components/app/app-modals/app-modals';
import recaptcha from '@/services/recaptcha';

const CAPTCHA_FIELD = 'g-recaptcha-response';
const CACHE_NAME = 'register';
const MAX_CHARACTERS_USERNAME_PART = 5;

export default {
  mixins: [
    formMixin,
    popupPageModeMixin,
    userAgentMixin,
  ],
  data() {
    return {
      // attempt counter for v2 fallback rerenders
      attempt: 1,
      usernameShowed: false,
      usernameSource: '',
      fieldTypePassword: 'password',
      latestEmail: '',
      fields: {
        email: '',
        emailConfirmation: '',
        password: '',
        username: '',
      },
      timeout: null,
      // Pending payload for v2 fallback retry
      pendingPayload: null,
    };
  },
  validations: {
    fields: {
      email: validators.email,
      emailConfirmation: validators.emailConfirmation('email'),
      password: validators.password,
      username: validators.username,
    },
  },
  computed: {
    ...mapState('popup', ['formDataCache']),
    showUsername() {
      if (this.usernameShowed) return true;
      const show = !this.$v.fields.email.$invalid && !this.$v.fields.emailConfirmation.$invalid;
      if (show) {
        if (this.fields.username === '') {
          const email = this.fields.email;
          this.usernameSource = snakeCase(email.substr(0, email.indexOf('@')));
          this.fields.username = this.suggestUserName();
          this.$v.fields.username.$touch();
          this.tabTo('_', 'password');
          this.usernameShowed = true;
        }
      } else {
        this.fields.username = '';
      }
      return show;
    },
    registerButtonDisabled() {
      return this.invalidOrDisabled;
    },
  },
  watch: {
    'fields.email'(value) {
      if (this.latestEmail && value && value !== this.latestEmail && this.formIsValid() && this.errors.length > 0) {
        this.formReset();
      }
    },
    'fields.emailConfirmation'(value) {
      if (this.latestEmail && value && value !== this.latestEmail && this.formIsValid() && this.errors.length > 0) {
        this.formReset();
      }
    },
  },
  methods: {
    ...mapActions('user', ['login', 'register', 'startRegistrationFlow']),
    ...mapActions('popup', ['setCurrentPopup', 'setCurrentSmallPopup', 'cacheFormData', 'setCurrentPopupOptions']),
    ...mapActions('profile', ['setProfileStatus']),
    showPassword() {
      this.fieldTypePassword = (this.fieldTypePassword === 'password') ? 'text' : 'password';
    },
    onLoginFail() {
      this.setCurrentPopup(MODALS.LOGIN);
    },

    // ========== LOGIN AFTER REGISTRATION ==========
    async loginAfterRegistration() {
      // Set registration flow flag - 2FA prompt will appear at end of registration flow
      this.startRegistrationFlow();

      try {
        // Get a FRESH v3 token for login (don't reuse registration token)
        const loginV3Token = await recaptcha.executeV3('login');

        const payload = {
          [CAPTCHA_FIELD]: loginV3Token,
          'recaptcha-version': 'v3',
          email: this.fields.email,
          password: this.fields.password,
          grant_type: 'password',
        };

        this.login(payload)
          .then(() => {
            // Close registration modal - app.js will handle showing ACCOUNT_VERIFY/Personal Details
            this.setCurrentPopup(null);
            if (this.isPage) {
              this.$router.push({ name: 'home' });
            }
          })
          .catch((error) => {
            // Check if v3 failed and v2 challenge is needed
            if (recaptcha.isV3ScoreFailure(error)) {
              this.pendingPayload = payload;
              this.attempt += 1;
              this.setCurrentPopup(MODALS.RECAPTCHA_CHALLENGE);
              this.setCurrentPopupOptions({
                attempt: this.attempt,
                onVerified: this.onRecaptchaV2VerifiedForLogin,
              });
              return;
            }
            // Other login error - show login modal
            this.onLoginFail();
          });
      } catch (err) {
        // Error getting v3 token for login - fall back to login modal
        this.formEnable();
        this.onLoginFail();
      }
    },

    // Called when user completes v2 challenge for login phase
    onRecaptchaV2VerifiedForLogin(v2Token) {
      const payload = {
        ...this.pendingPayload,
        [CAPTCHA_FIELD]: v2Token,
        'recaptcha-version': 'v2',
      };

      this.login(payload)
        .then(() => {
          this.setCurrentPopup(null);
          if (this.isPage) {
            this.$router.push({ name: 'home' });
          }
        })
        .catch((error) => {
          // v2 login failed - redirect to login modal with error
          // Use setCurrentPopupOptions to pass the error to display
          this.setCurrentPopup(MODALS.LOGIN);
          this.setCurrentPopupOptions({
            loginError: error,
          });
        });
    },

    // ========== REGISTRATION SUBMIT ==========
    async onSubmit() {
      this.$gtm.trackEvent({
        event: 'eventTrackerTrigger',
        category: 'Registration',
        action: 'submit',
        label: 'Create Account',
      });

      if (this.formIsValid() && this.formDisable()) {
        try {
          // Step 1: Get invisible v3 token for registration
          const v3Token = await recaptcha.executeV3('register');

          const payload = {
            recaptchaToken: v3Token,
            recaptchaVersion: 'v3',
            email: this.fields.email,
            password: this.fields.password,
            username: this.fields.username,
          };

          this.register(payload)
            .then(() => this.setProfileStatus({ event: 'registered' }))
            .then(() => this.loginAfterRegistration())
            .catch((e) => {
              // Check if v3 failed and v2 challenge is needed
              if (recaptcha.isV3ScoreFailure(e)) {
                this.pendingPayload = payload;
                this.attempt += 1;
                this.setCurrentPopup(MODALS.RECAPTCHA_CHALLENGE);
                this.setCurrentPopupOptions({
                  attempt: this.attempt,
                  onVerified: this.onRecaptchaV2VerifiedForRegistration,
                });
                this.formEnable();
                return;
              }

              // Handle other registration errors
              const data = get(e, 'response.data', []);
              const errorCodesEmailExist = ['emailAlreadyInUse'];
              const emailExistsIndex = findIndex(
                data, i => i.code && errorCodesEmailExist.includes(i.code));

              if (emailExistsIndex !== -1) {
                this.latestEmail = this.fields.email;
                this.setCurrentPopup(MODALS.ACCOUNT_ALREADY_EXISTS);
                this.setCurrentPopupOptions({ email: this.fields.email });
              } else {
                this.latestEmail = '';
              }

              this.attempt += 1;
              const errorCodesToSuggestUsername = ['restrictedTextInUsername', 'usernameAlreadyInUse'];
              const isUsernameTakenIndex = findIndex(
                data, i => i.code && errorCodesToSuggestUsername.includes(i.code));

              if (isUsernameTakenIndex !== -1) {
                this.fields.username = this.suggestUserName();
              }

              this.formEnable();
              this.formHandleErrors(e);
            });
        } catch (err) {
          this.formEnable();
          this.formHandleErrors(err);
        }
      }
    },

    // Called when user completes v2 challenge for registration phase
    onRecaptchaV2VerifiedForRegistration(v2Token) {
      this.formDisable();

      const payload = {
        ...this.pendingPayload,
        recaptchaToken: v2Token,
        recaptchaVersion: 'v2',
      };

      this.register(payload)
        .then(() => this.setProfileStatus({ event: 'registered' }))
        .then(() => this.loginAfterRegistration())
        .catch((e) => {
          // v2 registration failed - show error on registration form
          this.setCurrentPopup(MODALS.REGISTER);
          this.formEnable();
          this.formHandleErrors(e);
        });
    },
    showUsernameInfo() {
      this.setCurrentSmallPopup(MODALS.SMALL_USERNAME);
    },
    suggestUserName() {
      return `${this.usernameSource.slice(0, MAX_CHARACTERS_USERNAME_PART)}${this.randomNumber()}`;
    },
    randomNumber(min = 10000, max = 99999) {
      return Math.floor(Math.random() * (max - min + 1) + min);
    },
    onTermsAndConditions() {
      const termsAndConditionsRoute = 'terms-and-conditions';
      if (this.$router.currentRoute.name === termsAndConditionsRoute) {
        this.setCurrentPopup();
      } else {
        this.$router.push({ name: termsAndConditionsRoute });
      }
    },
  },
  created() {
    if (this.formDataCache !== null && CACHE_NAME === this.formDataCache.route) {
      this.fields = {
        ...this.fields,
        ...this.formDataCache,
      };
    }
  },
  beforeDestroy() {
    this.cacheFormData({ ...this.fields, route: CACHE_NAME });
  },
};



// WEBPACK FOOTER //
// ./src/components/account/account-register/account-register.js